import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  LinearProgress,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  alpha,
} from '@mui/material';
import {
  TrendingUp,
  CheckCircle,
  Lock,
  LockOpen,
  ArrowForward,
  Bolt,
  AccountBalance,
  AttachMoney,
  Schedule,
  Star,
  Verified,
  Upload,
  Description,
  KeyboardArrowRight,
  Close,
  OpenInNew,
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { BRAND } from '../components/login/brand';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

// Matches the sidebar "Upload Bank Statements" button.
const CTA_SX = {
  background: BRAND.gradient,
  color: '#fff',
  fontWeight: 700,
  borderRadius: '8px',
  boxShadow: '0 4px 14px rgba(172,104,46,0.35)',
  '&:hover': { background: BRAND.gradientHover, boxShadow: '0 6px 18px rgba(172,104,46,0.45)' },
  '&.Mui-disabled': { background: BRAND.border, color: BRAND.muted, boxShadow: 'none' },
};

const Programs = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [tierStatus, setTierStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [isGeneratingToken, setIsGeneratingToken] = useState(false);

  useEffect(() => {
    fetchTierStatus();
  }, []);

  const fetchTierStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/requirements/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.data.success) {
        setTierStatus(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching tier status:', error);
    } finally {
      setLoading(false);
    }
  };

  const buildRedirectUrl = async (baseUrl, appType = 'application') => {
    const url = new URL(baseUrl);
    const returnParam = appType === 'pfs' ? 'pfs_submitted=true' : 'submitted=true';
    const returnUrl = `${import.meta.env.VITE_CORELOANHUB_URL || 'http://localhost:3000'}/dashboard?${returnParam}&source=${appType === 'pfs' ? 'pfs-copilot' : 'financing-app'}`;
    url.searchParams.append('returnUrl', returnUrl);
    
    if (user?.email) {
      url.searchParams.append('email', user.email);
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/auth/generate-single-use-token`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      if (response.data.success) {
        url.searchParams.append('authToken', response.data.token);
      }
    } catch (error) {
      console.error('Failed to generate auth token:', error);
    }
    
    return url.toString();
  };

  const handleStartApplication = async () => {
    setIsGeneratingToken(true);
    try {
      const redirectUrl = await buildRedirectUrl(
        import.meta.env.VITE_FINANCING_APP_URL || 'https://app.corebusinesscapital.com'
      );
      window.open(redirectUrl, '_blank');
    } catch (error) {
      toast.error(t('programs.failedToOpenApplication'));
    } finally {
      setIsGeneratingToken(false);
    }
  };

  const handleStartPFS = async () => {
    setIsGeneratingToken(true);
    try {
      const redirectUrl = await buildRedirectUrl(
        import.meta.env.VITE_PFS_URL || 'https://pfs.corebusinesscapital.com',
        'pfs'
      );
      window.open(redirectUrl, '_blank');
    } catch (error) {
      toast.error(t('programs.failedToOpenPFS'));
    } finally {
      setIsGeneratingToken(false);
    }
  };

  // Tier definitions with unlocking requirements
  const tiers = [
    {
      id: 'BASIC',
      name: t('programs.quickFunding'),
      subtitle: t('programs.quickFundingSubtitle'),
      fundingRange: '$5K - $100K',
      fundingTime: '24-72 hours',
      color: '#9A5C27',
      accent: BRAND.sand,
      lightColor: 'rgba(232,189,118,0.16)',
      icon: <Bolt />,
      requirements: [
        { key: 'application', label: t('programs.businessApplication'), done: tierStatus?.hasApplication },
        { key: 'bankStatements', label: t('programs.bankStatements3'), done: (tierStatus?.documents?.business_bank_statement || 0) >= 3 },
      ],
      programs: [
        { name: t('programs.businessCashAdvance'), desc: t('programs.businessCashAdvanceDesc'), rate: '1.15-1.45 factor', term: '3-12 mo', features: [t('programs.noFixedPayments'), t('programs.approvalBasedOnRevenue'), t('programs.fastFunding')] },
        { name: t('programs.workingCapitalLoan'), desc: t('programs.workingCapitalLoanDesc'), rate: '8-29.99% APR', term: '6-24 mo', features: [t('programs.fixedMonthlyPayments'), t('programs.bridgeSeasonalGaps'), t('programs.buildCredit')] },
        { name: t('programs.shortTermLoan'), desc: t('programs.shortTermLoanDesc'), rate: '10-30% APR', term: '3-18 mo', features: [t('programs.24hourApproval'), t('programs.multiplePaymentOptions'), t('programs.simpleApplication')] },
        { name: t('programs.lineOfCredit'), desc: t('programs.lineOfCreditDesc'), rate: '7-20% APR', term: 'Revolving', features: [t('programs.payOnlyForWhatYouUse'), t('programs.reusableCredit'), t('programs.flexibleAccess')] },
      ]
    },
    {
      id: 'ENHANCED',
      name: t('programs.growthFinancing'),
      subtitle: t('programs.growthFinancingSubtitle'),
      fundingRange: '$100K - $500K',
      fundingTime: '3-7 business days',
      color: '#7E4A1E',
      accent: BRAND.copper,
      lightColor: 'rgba(190,125,55,0.12)',
      icon: <TrendingUp />,
      requirements: [
        { key: 'application', label: t('programs.businessApplication'), done: tierStatus?.hasApplication },
        { key: 'bankStatements', label: t('programs.bankStatements6'), done: (tierStatus?.documents?.business_bank_statement || 0) >= 6 },
        { key: 'taxReturns', label: t('programs.taxReturns1Year'), done: (tierStatus?.documents?.business_tax_return || 0) >= 1 },
      ],
      programs: [
        { name: t('programs.termLoan'), desc: t('programs.termLoanDesc'), rate: '6-18% APR', term: '1-5 years', features: [t('programs.lowerRatesAvailable'), t('programs.predictablePayments'), t('programs.largerAmounts')] },
        { name: t('programs.equipmentFinancing'), desc: t('programs.equipmentFinancingDesc'), rate: '5-15% APR', term: '2-7 years', features: [t('programs.equipmentAsCollateral'), t('programs.taxBenefits'), t('programs.hundredPercentFinancing')] },
        { name: t('programs.invoiceFinancing'), desc: t('programs.invoiceFinancingDesc'), rate: '1-5%/mo', term: '30-90 days', features: [t('programs.unlockReceivables'), t('programs.noNewDebt'), t('programs.growWithoutLimits')] },
        { name: t('programs.payrollFinancing'), desc: t('programs.payrollFinancingDesc'), rate: '1.5-4%/cycle', term: 'Ongoing', features: [t('programs.meetPayrollOnTime'), t('programs.scaleWorkforce'), t('programs.flexibleTerms')] },
      ]
    },
    {
      id: 'SBA',
      name: t('programs.sbaLoans'),
      subtitle: t('programs.sbaLoansSubtitle'),
      fundingRange: '$50K - $5M+',
      fundingTime: '45-90 days',
      color: BRAND.brown,
      accent: BRAND.brown,
      lightColor: 'rgba(72,39,15,0.07)',
      icon: <AccountBalance />,
      requirements: [
        { key: 'application', label: t('programs.businessApplication'), done: tierStatus?.hasApplication },
        { key: 'pfs', label: t('programs.personalFinancialStatement'), done: tierStatus?.hasPFS },
        { key: 'bankStatements', label: t('programs.bankStatements6'), done: (tierStatus?.documents?.business_bank_statement || 0) >= 6 },
        { key: 'businessTax', label: t('programs.businessTaxReturns2Years'), done: (tierStatus?.documents?.business_tax_return || 0) >= 2 },
        { key: 'personalTax', label: t('programs.personalTaxReturns2Years'), done: (tierStatus?.documents?.personal_tax_return || 0) >= 2 },
        { key: 'financials', label: t('programs.plAndBalanceSheet'), done: tierStatus?.documents?.profit_loss && tierStatus?.documents?.balance_sheet },
      ],
      programs: [
        { name: t('programs.sba7aLoan'), desc: t('programs.sba7aLoanDesc'), rate: 'Prime + 2.25-4.75%', term: 'Up to 25 years', features: [t('programs.mostVersatileSBA'), t('programs.lowerDownPayments'), t('programs.longerRepaymentTerms')] },
        { name: t('programs.sba504Loan'), desc: t('programs.sba504LoanDesc'), rate: 'Below market rates', term: '10-25 years', features: [t('programs.fixedInterestRates'), t('programs.upTo90Financing'), t('programs.jobCreationFocus')] },
        { name: t('programs.sbaExpress'), desc: t('programs.sbaExpressDesc'), rate: 'Prime + 4.5-6.5%', term: 'Up to 10 years', features: [t('programs.fastApproval'), t('programs.lessPaperwork'), t('programs.linesOfCreditAvailable')] },
        { name: t('programs.sbaMicroloan'), desc: t('programs.sbaMicroloanDesc'), rate: '8-13%', term: 'Up to 6 years', features: [t('programs.perfectForStartups'), t('programs.technicalAssistance'), t('programs.smallerAmounts')] },
      ]
    }
  ];

  const calculateTierProgress = (tier) => {
    const completed = tier.requirements.filter(r => r.done).length;
    return Math.round((completed / tier.requirements.length) * 100);
  };

  const isTierUnlocked = (tier) => {
    return tier.requirements.every(r => r.done);
  };

  const getCurrentTierIndex = () => {
    for (let i = tiers.length - 1; i >= 0; i--) {
      if (isTierUnlocked(tiers[i])) return i;
    }
    return -1;
  };

  const getNextAction = () => {
    if (!tierStatus?.hasApplication) {
      return { action: t('programs.completeYourApplication'), type: 'application' };
    }
    if ((tierStatus?.documents?.business_bank_statement || 0) < 3) {
      return { action: t('programs.upload3BankStatements'), type: 'documents' };
    }
    if (!isTierUnlocked(tiers[0])) {
      return { action: t('programs.completeBasicRequirements'), type: 'documents' };
    }
    if (!isTierUnlocked(tiers[1])) {
      return { action: t('programs.uploadAdditionalDocs'), type: 'documents' };
    }
    if (!tierStatus?.hasPFS) {
      return { action: t('programs.completePFSForSBA'), type: 'pfs' };
    }
    return null;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const currentTierIndex = getCurrentTierIndex();
  const nextAction = getNextAction();

  return (
    <Box sx={{ pb: 6 }}>
      <Container maxWidth="lg" disableGutters>
        {/* Hero */}
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            background: BRAND.heroGradient,
            color: '#fff',
            borderRadius: '12px',
            px: { xs: 3, md: 5 },
            pt: { xs: 4, md: 5 },
            pb: nextAction ? { xs: 7, md: 8 } : { xs: 4, md: 5 },
            mb: nextAction ? 0 : 4,
          }}
        >
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: BRAND.gradient }} />
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              right: -120,
              top: -120,
              width: 380,
              height: 380,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(232,189,118,0.22) 0%, rgba(232,189,118,0) 70%)',
              pointerEvents: 'none',
            }}
          />
          <Box sx={{ position: 'relative' }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 800, mb: 1, color: '#fff', fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
              {t('programs.financingPrograms')}
            </Typography>
            <Typography sx={{ color: 'rgba(255,248,238,0.82)', fontSize: '1.05rem', mb: 3, maxWidth: 560 }}>
              {t('programs.unlockFunding')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
              {[
                { Icon: AttachMoney, key: 'availableAmount' },
                { Icon: Schedule, key: 'fundingAsFast' },
                { Icon: Verified, key: 'financingOptions' },
              ].map(({ Icon, key }) => (
                <Box
                  key={key}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: '999px',
                    bgcolor: 'rgba(232,189,118,0.12)',
                    border: '1px solid rgba(232,189,118,0.28)',
                  }}
                >
                  <Icon sx={{ fontSize: 18, color: BRAND.sand }} />
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#FFF8EE' }}>
                    {t(`programs.${key}`)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Next Action Card */}
        {nextAction && (
          <Paper
            elevation={0}
            sx={{
              position: 'relative',
              mx: { xs: 1.5, md: 4 },
              mt: { xs: -4, md: -5 },
              mb: 4,
              p: { xs: 2.5, md: 3 },
              borderRadius: '10px',
              bgcolor: '#fff',
              border: `1px solid ${BRAND.border}`,
              borderLeft: `4px solid ${BRAND.copper}`,
              boxShadow: '0 10px 30px rgba(46,24,8,0.12)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                <Box sx={{
                  width: 48,
                  height: 48,
                  flexShrink: 0,
                  borderRadius: '50%',
                  bgcolor: 'rgba(232,189,118,0.25)',
                  color: BRAND.copperDark,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Star />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: BRAND.copperDark }}>
                    {t('programs.nextStepUnlock')}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND.ink, lineHeight: 1.3 }}>
                    {nextAction.action}
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                onClick={() => {
                  if (nextAction.type === 'application') handleStartApplication();
                  else if (nextAction.type === 'pfs') handleStartPFS();
                  else navigate('/documents');
                }}
                disabled={isGeneratingToken}
                sx={{ ...CTA_SX, px: 4 }}
              >
                {isGeneratingToken ? t('common.loading') : t('programs.takeAction')}
              </Button>
            </Box>
          </Paper>
        )}

        {/* Tier Cards */}
        <Grid container spacing={3}>
          {tiers.map((tier, index) => {
            const isUnlocked = isTierUnlocked(tier);
            const progress = calculateTierProgress(tier);
            const isCurrentGoal = index === currentTierIndex + 1;

            return (
              <Grid item xs={12} md={4} key={tier.id}>
                <Card
                  elevation={0}
                  onClick={!isUnlocked ? () => navigate('/documents') : undefined}
                  sx={{
                    height: '100%',
                    position: 'relative',
                    overflow: 'visible',
                    borderRadius: '10px',
                    border: `1px solid ${isUnlocked ? alpha(tier.accent, 0.55) : BRAND.border}`,
                    borderTop: `4px solid ${tier.accent}`,
                    boxShadow: isUnlocked ? '0 8px 24px rgba(172,104,46,0.18)' : '0 1px 3px rgba(0,0,0,0.06)',
                    transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                    cursor: !isUnlocked ? 'pointer' : 'default',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 28px rgba(46,24,8,0.14)',
                    },
                  }}
                >
                  {/* Status Badge */}
                  {isUnlocked && (
                    <Chip
                      icon={<LockOpen sx={{ fontSize: 16, color: `${BRAND.sand} !important` }} />}
                      label={t('programs.unlocked')}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: -14,
                        right: 16,
                        bgcolor: BRAND.brown,
                        color: BRAND.sand,
                        fontWeight: 700,
                      }}
                    />
                  )}
                  {isCurrentGoal && !isUnlocked && (
                    <Chip
                      icon={<Star sx={{ fontSize: 16, color: '#fff !important' }} />}
                      label={t('programs.nextGoal')}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: -14,
                        right: 16,
                        background: BRAND.gradient,
                        color: '#fff',
                        fontWeight: 700,
                        boxShadow: '0 4px 10px rgba(172,104,46,0.35)',
                      }}
                    />
                  )}

                  <CardContent sx={{ p: 3 }}>
                    {/* Tier Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
                      <Box sx={{
                        width: 48,
                        height: 48,
                        flexShrink: 0,
                        borderRadius: '50%',
                        bgcolor: tier.lightColor,
                        color: tier.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        {tier.icon}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: tier.color }}>
                          {t('programs.tierLabel', { n: index + 1 })}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND.ink, lineHeight: 1.25 }}>
                          {tier.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {tier.subtitle}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Funding Info */}
                    <Box sx={{
                      bgcolor: tier.lightColor,
                      border: `1px solid ${alpha(tier.accent, 0.35)}`,
                      borderRadius: '8px',
                      p: 2,
                      mb: 3,
                      textAlign: 'center',
                    }}>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: tier.color }}>
                        {tier.fundingRange}
                      </Typography>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                        <Schedule sx={{ fontSize: 15 }} />
                        <Typography variant="body2">
                          {t('programs.fundingIn', { time: tier.fundingTime })}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Progress */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: BRAND.ink }}>
                          {t('programs.requirements')}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: tier.color }}>
                          {progress}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: 'rgba(72,39,15,0.08)',
                          '& .MuiLinearProgress-bar': {
                            background: tier.id === 'SBA' ? BRAND.heroGradient : BRAND.gradient,
                            borderRadius: 4,
                          },
                        }}
                      />
                    </Box>

                    {/* Requirements Checklist */}
                    <Box sx={{ mb: 2 }}>
                      {tier.requirements.map((req) => (
                        <Box
                          key={req.key}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            py: 0.75,
                          }}
                        >
                          {req.done ? (
                            <CheckCircle sx={{ fontSize: 20, color: 'success.main' }} />
                          ) : (
                            <Box sx={{
                              width: 20,
                              height: 20,
                              flexShrink: 0,
                              borderRadius: '50%',
                              border: '2px solid #D6CEC4',
                            }} />
                          )}
                          <Typography
                            variant="body2"
                            sx={{
                              color: req.done ? 'text.primary' : 'text.secondary',
                              fontWeight: req.done ? 500 : 400,
                            }}
                          >
                            {req.label}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    <Divider sx={{ my: 2, borderColor: BRAND.border }} />

                    {/* Programs List */}
                    <Typography sx={{ mb: 1, fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: BRAND.muted }}>
                      {t('programs.availablePrograms')}
                    </Typography>
                    {tier.programs.map((program, i) => (
                      <Box
                        key={i}
                        onClick={() => isUnlocked && setSelectedProgram({ ...program, tier })}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          py: 1,
                          px: 1.5,
                          mx: -1.5,
                          borderRadius: '6px',
                          cursor: isUnlocked ? 'pointer' : 'default',
                          transition: 'background-color 0.2s',
                          '&:hover': isUnlocked ? { bgcolor: tier.lightColor } : {},
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {isUnlocked ? (
                            <CheckCircle sx={{ fontSize: 16, color: tier.color }} />
                          ) : (
                            <Lock sx={{ fontSize: 16, color: '#B5ADA3' }} />
                          )}
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              color: isUnlocked ? 'text.primary' : 'text.secondary',
                            }}
                          >
                            {program.name}
                          </Typography>
                        </Box>
                        {isUnlocked && (
                          <KeyboardArrowRight sx={{ fontSize: 18, color: tier.color }} />
                        )}
                      </Box>
                    ))}

                    {/* Upload prompt for locked tiers */}
                    {!isUnlocked && (
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        mt: 2,
                        py: 1.25,
                        borderRadius: '8px',
                        border: `1px dashed ${alpha(BRAND.copperDark, 0.4)}`,
                        bgcolor: 'rgba(232,189,118,0.06)',
                        color: BRAND.copperDark,
                        transition: 'background-color 0.2s',
                        '&:hover': { bgcolor: 'rgba(232,189,118,0.14)' },
                      }}>
                        <Upload sx={{ fontSize: 18 }} />
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {t('programs.uploadToUnlock')}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Bottom CTA */}
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            mt: 6,
            p: { xs: 3, md: 5 },
            borderRadius: '12px',
            background: BRAND.heroGradient,
            color: '#fff',
            textAlign: 'center',
          }}
        >
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: BRAND.gradient }} />
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              left: -120,
              bottom: -160,
              width: 380,
              height: 380,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(232,189,118,0.18) 0%, rgba(232,189,118,0) 70%)',
              pointerEvents: 'none',
            }}
          />
          <Box sx={{ position: 'relative' }}>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 800, mb: 1.5, color: '#fff' }}>
              {t('programs.readyToGetStarted')}
            </Typography>
            <Typography sx={{ mb: 3, color: 'rgba(255,248,238,0.82)', maxWidth: 600, mx: 'auto' }}>
              {t('programs.completeApplicationToday')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              {!tierStatus?.hasApplication && (
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleStartApplication}
                  disabled={isGeneratingToken}
                  startIcon={<Description />}
                  sx={{ ...CTA_SX, px: 4, py: 1.5 }}
                >
                  {t('programs.startApplication')}
                </Button>
              )}
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/documents')}
                startIcon={<Upload />}
                sx={{
                  borderColor: 'rgba(232,189,118,0.6)',
                  color: BRAND.sand,
                  borderRadius: '8px',
                  fontWeight: 700,
                  px: 4,
                  py: 1.5,
                  '&:hover': { bgcolor: 'rgba(232,189,118,0.12)', borderColor: BRAND.sand },
                }}
              >
                {t('programs.uploadDocuments')}
              </Button>
            </Box>
          </Box>
        </Box>
      </Container>

      {/* Program Detail Dialog */}
      <Dialog
        open={!!selectedProgram}
        onClose={() => setSelectedProgram(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', overflow: 'hidden' } }}
      >
        {selectedProgram && (
          <>
            <DialogTitle sx={{
              position: 'relative',
              background: BRAND.heroGradient,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              pt: 3,
            }}>
              <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: BRAND.gradient }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff' }}>
                  {selectedProgram.name}
                </Typography>
                <Typography variant="body2" sx={{ color: BRAND.sand }}>
                  {selectedProgram.tier.name}
                </Typography>
              </Box>
              <IconButton onClick={() => setSelectedProgram(null)} sx={{ color: 'rgba(255,248,238,0.85)' }}>
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: '24px !important' }}>
              <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                {selectedProgram.desc}
              </Typography>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                  { label: t('programs.rate'), value: selectedProgram.rate },
                  { label: t('programs.term'), value: selectedProgram.term },
                ].map(({ label, value }) => (
                  <Grid item xs={6} key={label}>
                    <Box sx={{
                      p: 2,
                      textAlign: 'center',
                      borderRadius: '8px',
                      bgcolor: selectedProgram.tier.lightColor,
                      border: `1px solid ${alpha(selectedProgram.tier.accent, 0.35)}`,
                    }}>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: BRAND.muted }}>
                        {label}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: selectedProgram.tier.color }}>
                        {value}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {/* Key Features */}
              {selectedProgram.features && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: BRAND.ink }}>
                    {t('programs.keyFeatures')}
                  </Typography>
                  {selectedProgram.features.map((feature, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
                      <CheckCircle sx={{ fontSize: 18, color: selectedProgram.tier.color }} />
                      <Typography variant="body2">{feature}</Typography>
                    </Box>
                  ))}
                </Box>
              )}

              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>{t('programs.youreEligible')}</strong> {t('programs.youveUnlockedProgram')}
                </Typography>
              </Alert>
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 0 }}>
              <Button onClick={() => setSelectedProgram(null)} sx={{ color: BRAND.body }}>
                {t('common.close')}
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  setSelectedProgram(null);
                  handleStartApplication();
                }}
                disabled={isGeneratingToken}
                endIcon={<OpenInNew />}
                sx={{ ...CTA_SX, px: 3 }}
              >
                {t('programs.applyNow')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default Programs;
